import { useEffect, useState, useMemo } from "react";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useBusy } from "../../context/BusyContext";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";

interface DropdownOption {
  id: number;
  label: string;
  selling_market: "online" | "offline";
}

export default function Subcategories() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const [data, setData] = useState<any[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<"online" | "offline">(
    "online",
  );
  const [categories, setCategories] = useState<DropdownOption[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  /* =========================
     HELPER FUNCTION
  ========================= */
  const openSubcategoryModal = (mode: "view" | "edit", row: any) => {
    const market: "online" | "offline" =
      row.selling_market === "offline" ? "offline" : "online";

    setModalMode(mode);
    setSelectedMarket(market);
    setActiveRow({
      ...row,
      selling_market: market,
      categoryId: Number(row.categoryId),
    });
    setModalOpen(true);
  };

  /* =========================
     FIELDS (useMemo for dropdown update)
  ========================= */
  const subcategoryFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      {
        key: "selling_market",
        label: "Selling Market",
        type: "select",
        options: [
          { label: "Online", value: "online" },
          { label: "Offline", value: "offline" },
        ],
        colSpan: 2,
      },
      {
        key: "categoryId",
        label: "Category",
        type: "select",
        options: categories
          .filter((category) => category.selling_market === selectedMarket)
          .map((category) => ({
            label: category.label,
            value: category.id,
          })),
        colSpan: 2,
      },
      { key: "name", label: "Name", colSpan: 2 },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        imageType: "single",
        imageFolder: "subcategories",
        uploadField: "subCategoryImage",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [categories, selectedMarket],
  );

  /* =========================
     COLUMNS
  ========================= */
  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "category_name", label: "Category" },
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    {
      key: "selling_market",
      label: "Selling Market",
      render: (row) => (
        <span
          className={
            row.selling_market === "offline" ? "badge-warning" : "badge-success"
          }
        >
          {row.selling_market === "offline" ? "Offline" : "Online"}
        </span>
      ),
    },
    {
      key: "image",
      label: "Image",
      imageFolder: "subcategories",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  /* =========================
     FETCH SUBCATEGORIES
  ========================= */
  const fetchSubcategories = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/subcategories`, {
        credentials: "include",
      });
      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Subcategories Error:", error);
    }
  };

  /* =========================
     FETCH CATEGORIES (DROPDOWN)
  ========================= */
  const fetchCategories = async () => {
    try {
      const res = await runBusy(() =>
        fetch(`${BASE_URL}/admin/categories`, {
          credentials: "include",
        }),
      );

      const result = await res.json();

      if (result.success) {
        setCategories(
          result.data.map((category: any) => ({
            id: category.id,
            label: category.category_name,
            selling_market: category.selling_market || "online",
          })),
        );
      }
    } catch (error) {
      console.error("Fetch Categories Error:", error);
    }
  };

  useEffect(() => {
    fetchSubcategories();
    fetchCategories();
  }, []);

  const buildFormData = (
    formData: any,
    fields: FormField[],
    activeRow?: any,
  ) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      /* =========================
       PRODUCT IMAGES (MULTIPLE)
    ========================= */

      if (field.type === "image" && field.imageType === "product") {
        if (Array.isArray(value)) {
          value.forEach((img: any, index: number) => {
            // Upload new image
            if (img?.file) {
              form.append(field.uploadField || "subCategoryImage", img.file);
            }

            // Remove existing image
            if (!img && activeRow?.images?.[index]?.id) {
              form.append("removeImages[]", activeRow.images[index].id);
            }
          });
        }
      } else if (field.type === "image") {
        /* =========================
       SINGLE IMAGE
    ========================= */
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value === "") {
          form.append(field.key, "REMOVE");
        }
      } else {
        /* =========================
       NORMAL FIELDS
    ========================= */
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  /* =========================
     ADD / UPDATE
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, subcategoryFields, activeRow);

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/subcategories`
          : `${BASE_URL}/admin/subcategories/${activeRow.id}`;

      const res = await runBusy(() =>
        fetch(url, {
          method,
          credentials: "include",
          body: form,
        }),
      );

      const result = await res.json();

      if (!res.ok || result.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message:
            result.message ||
            (modalMode === "add"
              ? "Failed to add subcategory."
              : "Failed to update subcategory."),
          confirmText: "OK",
        });
        return;
      }

      await fetchSubcategories();

      setModalOpen(false);
      setActiveRow(null);

      showModal({
        type: "success",
        title: "Success",
        message:
          result.message ||
          (modalMode === "add"
            ? "Subcategory added successfully."
            : "Subcategory updated successfully."),
        confirmText: "OK",
      });
    } catch (error: any) {
      console.error("Submit Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.message ||
          (modalMode === "add"
            ? "Failed to add subcategory."
            : "Failed to update subcategory."),
        confirmText: "OK",
      });
    }
  };

  /* =========================
     DELETE
  ========================= */
  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Subcategory",
      message: `Are you sure you want to delete "${row.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/subcategories/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchSubcategories();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Subcategory deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Subcategories"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setSelectedMarket("online");
          setActiveRow({
            id: "",
            selling_market: "online",
            categoryId: "",
            name: "",
            description: "",
            image: "",
            status: "active",
          });
          setModalOpen(true);
        }}
        onView={(row) => openSubcategoryModal("view", row)}
        onEdit={(row) => openSubcategoryModal("edit", row)}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="subcategories"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      {modalOpen && activeRow && (
        <AdminDataModal
          key={`${modalMode}-${activeRow.id || "new"}-${activeRow.selling_market}`}
          isOpen={modalOpen}
          mode={modalMode}
          title="Subcategory"
          fields={subcategoryFields}
          rowData={activeRow}
          onClose={() => {
            setModalOpen(false);
            setActiveRow(null);
          }}
          onSubmit={handleSubmit}
          onFormChange={(formData) => {
            const market: "online" | "offline" =
              formData.selling_market === "offline" ? "offline" : "online";

            setSelectedMarket(market);
          }}
        />
      )}
    </div>
  );
}
